import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, TextField,
  Button, Avatar, Alert, Grid, IconButton, Tabs, Tab,
  Select, MenuItem, FormControl, InputLabel, Chip,
  LinearProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import * as XLSX from 'xlsx';
import { cambiarPassword, subirFoto, getDiasDisponibles, getSolicitudes,
  crearSolicitud, resolverSolicitud, getFichajeHoy, ficharEntrada,
  ficharPausaInicio, ficharPausaFin, ficharSalida, getHistorialFichajes,
  getOrganigrama, getDocumentacion, descargarDocumentacion, getFotoPerfilUrl, estado2FA, iniciar2FA, confirmar2FA, desactivar2FA } from '../api';

import FichajeOlvidado from '../components/FichajeOlvidado';

export default function Perfil() {
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
  const [tab, setTab] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [mensaje, setMensaje] = useState<{tipo: 'success'|'error', texto: string} | null>(null);

  // Contraseña
  const [passwordActual, setPasswordActual] = useState('');
  const [passwordNueva, setPasswordNueva] = useState('');
  const [passwordConfirmar, setPasswordConfirmar] = useState('');

  // RRHH
  const [diasDisponibles, setDiasDisponibles] = useState<any>(null);
  const [solicitudes, setSolicitudes] = useState<any[]>([]);
  const [nuevaSolicitud, setNuevaSolicitud] = useState({ tipo: 'vacaciones', fecha_inicio: '', fecha_fin: '', motivo: '' });
  const [fichajeHoy, setFichajeHoy] = useState<any>(null);
  const [historialFichajes, setHistorialFichajes] = useState<any[]>([]);
  const [organigrama, setOrganigrama] = useState<any[]>([]);
  const [fichajeFiltros, setFichajeFiltros] = useState({ desde: '', hasta: '' });
  const [miDocumentacion, setMiDocumentacion] = useState<any[]>([]);

  const esAdmin = user.rol === 'admin';
  // Puede aprobar si entre las solicitudes visibles hay alguna que no es suya
  const puedeAprobar = solicitudes.some(s => s.tecnico_id !== user.id);

  const cargarDatos = () => {
    getDiasDisponibles().then(setDiasDisponibles);
    getSolicitudes().then(setSolicitudes);
    getFichajeHoy().then(setFichajeHoy);
    getHistorialFichajes().then(setHistorialFichajes);
    getOrganigrama().then(setOrganigrama).catch(() => {});
    getDocumentacion(user.id).then(setMiDocumentacion).catch(() => setMiDocumentacion([]));
  };

  useEffect(() => { cargarDatos(); }, []);

  const handleCambiarPassword = async () => {
    setMensaje(null);
    if (!passwordActual || !passwordNueva || !passwordConfirmar) {
      setMensaje({ tipo: 'error', texto: 'Rellena todos los campos' });
      return;
    }
    if (passwordNueva !== passwordConfirmar) {
      setMensaje({ tipo: 'error', texto: 'Las contraseñas nuevas no coinciden' });
      return;
    }
    try {
      await cambiarPassword({ password_actual: passwordActual, password_nueva: passwordNueva });
      setMensaje({ tipo: 'success', texto: '✅ Contraseña cambiada correctamente' });
      setPasswordActual(''); setPasswordNueva(''); setPasswordConfirmar('');
    } catch (e: any) {
      setMensaje({ tipo: 'error', texto: e.response?.data?.detail || 'Error al cambiar la contraseña' });
    }
  };

  const handleFotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubiendo(true);
    try {
      const result = await subirFoto(file);
      const updatedUser = { ...user, foto: result.foto };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      window.location.reload();
    } catch {
      setMensaje({ tipo: 'error', texto: 'Error subiendo la foto' });
    } finally {
      setSubiendo(false);
    }
  };

  const handleCrearSolicitud = async () => {
    if (!nuevaSolicitud.fecha_inicio || !nuevaSolicitud.fecha_fin) {
      alert('Selecciona las fechas');
      return;
    }
    try {
      await crearSolicitud(nuevaSolicitud);
      setNuevaSolicitud({ tipo: 'vacaciones', fecha_inicio: '', fecha_fin: '', motivo: '' });
      getSolicitudes().then(setSolicitudes);
      getDiasDisponibles().then(setDiasDisponibles);
      alert('✅ Solicitud enviada correctamente');
    } catch (e) {
      alert('Error al enviar la solicitud');
    }
  };

  const handleResolver = async (id: number, estado: string) => {
    try {
      await resolverSolicitud(id, { estado });
      getSolicitudes().then(setSolicitudes);
    } catch {
      alert('Error al resolver la solicitud');
    }
  };

  const handleFichar = async (accion: 'entrada' | 'pausa-inicio' | 'pausa-fin' | 'salida') => {
    try {
      const fns: any = { entrada: ficharEntrada, 'pausa-inicio': ficharPausaInicio, 'pausa-fin': ficharPausaFin, salida: ficharSalida };
      const result = await fns[accion]();
      getFichajeHoy().then(setFichajeHoy);
      getHistorialFichajes().then(setHistorialFichajes);
      if (result.horas_totales) {
        alert(`✅ Salida registrada — ${result.horas_totales}h trabajadas hoy`);
      }
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al fichar');
    }
  };

  // Aplicar filtro de fechas al historial de fichajes
  const fichajesFiltrados = historialFichajes.filter(f => {
    if (fichajeFiltros.desde && f.fecha < fichajeFiltros.desde) return false;
    if (fichajeFiltros.hasta && f.fecha > fichajeFiltros.hasta) return false;
    return true;
  });

  // Total de horas del periodo filtrado
  const totalHorasPeriodo = fichajesFiltrados.reduce(
    (sum, f) => sum + (f.horas_totales || 0), 0
  );

  // Filtro rápido por mes
  const aplicarMes = (offset: number) => {
    const ahora = new Date();
    const año = ahora.getFullYear();
    const mes = ahora.getMonth() + offset;
    const primero = new Date(año, mes, 1);
    const ultimo = new Date(año, mes + 1, 0);
    const fmt = (d: Date) => d.toISOString().slice(0, 10);
    setFichajeFiltros({ desde: fmt(primero), hasta: fmt(ultimo) });
  };

  const exportarFichajesExcel = () => {
    const datos = fichajesFiltrados.map(f => ({
      'Fecha': f.fecha,
      'Entrada': f.hora_entrada || '—',
      'Inicio pausa': f.hora_inicio_pausa || '—',
      'Fin pausa': f.hora_fin_pausa || '—',
      'Salida': f.hora_salida || '—',
      'Horas totales': f.horas_totales || 0,
    }));
    const ws = XLSX.utils.json_to_sheet(datos);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Fichajes');
    XLSX.writeFile(wb, `fichajes_${user.nombre}_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const estadoColor: any = { pendiente: 'warning', aprobada: 'success', rechazada: 'error' };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3, fontSize: { xs: '1.5rem', md: '2rem' } }}>
        👤 Mi perfil
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, textAlign: 'center', p: 2 }}>
            <CardContent>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Avatar
                  src={user.foto ? `${process.env.REACT_APP_API_URL}/perfil/foto/${user.id}` : ''}
                  sx={{ width: 100, height: 100, mx: 'auto', bgcolor: '#4f46e5', fontSize: 36 }}
                >
                  {user.nombre?.[0]}
                </Avatar>
                <IconButton
                  size="small" onClick={() => fileInputRef.current?.click()}
                  sx={{ position: 'absolute', bottom: 0, right: 0, bgcolor: '#4f46e5', color: 'white', '&:hover': { bgcolor: '#3730a3' } }}
                  disabled={subiendo}
                >
                  <PhotoCameraIcon fontSize="small" />
                </IconButton>
                <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFotoChange} />
              </Box>
              <Typography sx={{ mt: 2, fontWeight: 600 }}>{user.nombre} {user.apellidos}</Typography>
              <Typography variant="body2" color="text.secondary">{user.email}</Typography>
              <Typography variant="caption" sx={{
                display: 'inline-block', mt: 1.2, px: 2, py: 0.6, borderRadius: 10,
                fontWeight: 600, letterSpacing: 0.2,
                bgcolor: 'rgba(245,194,0,0.14)',
                border: '1px solid rgba(245,194,0,0.45)',
                color: (t) => t.palette.mode === 'dark' ? '#F5C200' : '#8A6D00',
              }}>
                {user.puesto || 'Sin puesto asignado'}{user.rol === 'admin' ? ' · 🔑 Admin' : ''}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <FichajeOlvidado onRegistrado={() => getHistorialFichajes().then(setHistorialFichajes)} />
              <Typography variant="h6" gutterBottom>🕐 Fichaje de hoy</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Entrada</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{fichajeHoy?.hora_entrada || '—'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Inicio pausa</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{fichajeHoy?.hora_inicio_pausa || '—'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Fin pausa</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{fichajeHoy?.hora_fin_pausa || '—'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Salida</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{fichajeHoy?.hora_salida || '—'}</Typography>
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="contained" disabled={!!fichajeHoy?.hora_entrada} onClick={() => handleFichar('entrada')} sx={{ bgcolor: '#22c55e' }}>
                  Fichar entrada
                </Button>
                <Button variant="outlined" disabled={!fichajeHoy?.hora_entrada || !!fichajeHoy?.hora_inicio_pausa} onClick={() => handleFichar('pausa-inicio')}>
                  Iniciar pausa
                </Button>
                <Button variant="outlined" disabled={!fichajeHoy?.hora_inicio_pausa || !!fichajeHoy?.hora_fin_pausa} onClick={() => handleFichar('pausa-fin')}>
                  Fin pausa
                </Button>
                <Button variant="contained" disabled={!fichajeHoy?.hora_entrada || !!fichajeHoy?.hora_salida} onClick={() => handleFichar('salida')} sx={{ bgcolor: '#ef4444' }}>
                  Fichar salida
                </Button>
              </Box>
              {fichajeHoy?.horas_totales && (
                <Chip label={`Total hoy: ${fichajeHoy.horas_totales}h`} color="info" sx={{ mt: 2 }} />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 3 }}>
        <Tab label="🌴 Ausencias" />
        <Tab label="🕐 Jornada" />
        <Tab label="🏢 Empresa" />
        <Tab label="🔒 Seguridad" />
        {puedeAprobar && <Tab label="✅ Aprobar solicitudes" />}
      </Tabs>

      {/* TAB VACACIONES */}
      {tab === 0 && diasDisponibles && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>🌴 Vacaciones</Typography>
                <Typography variant="body2" color="text.secondary">
                  {diasDisponibles.vacaciones_disponibles} de {diasDisponibles.vacaciones_totales} días disponibles
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(diasDisponibles.vacaciones_usadas / diasDisponibles.vacaciones_totales) * 100}
                  sx={{ mt: 1, mb: 2, height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption">Usados: {diasDisponibles.vacaciones_usadas} días</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>📅 Libre disposición</Typography>
                <Typography variant="body2" color="text.secondary">
                  {diasDisponibles.libre_disponibles} de {diasDisponibles.libre_totales} días disponibles
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={diasDisponibles.libre_totales > 0 ? (diasDisponibles.libre_usados / diasDisponibles.libre_totales) * 100 : 0}
                  sx={{ mt: 1, mb: 2, height: 8, borderRadius: 4 }}
                  color="secondary"
                />
                <Typography variant="caption">Usados: {diasDisponibles.libre_usados} días</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>➕ Solicitar días</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                      <InputLabel>Tipo</InputLabel>
                      <Select value={nuevaSolicitud.tipo} label="Tipo"
                        onChange={(e) => setNuevaSolicitud({ ...nuevaSolicitud, tipo: e.target.value })}>
                        <MenuItem value="vacaciones">Vacaciones</MenuItem>
                        <MenuItem value="libre">Libre disposición</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField fullWidth label="Desde" type="date" InputLabelProps={{ shrink: true }}
                      value={nuevaSolicitud.fecha_inicio}
                      onChange={(e) => setNuevaSolicitud({ ...nuevaSolicitud, fecha_inicio: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField fullWidth label="Hasta" type="date" InputLabelProps={{ shrink: true }}
                      value={nuevaSolicitud.fecha_fin}
                      onChange={(e) => setNuevaSolicitud({ ...nuevaSolicitud, fecha_fin: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button fullWidth variant="contained" sx={{ bgcolor: '#4f46e5', height: '100%' }} onClick={handleCrearSolicitud}>
                      Solicitar
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Motivo (opcional)" value={nuevaSolicitud.motivo}
                      onChange={(e) => setNuevaSolicitud({ ...nuevaSolicitud, motivo: e.target.value })} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* MIS SOLICITUDES (dentro de Ausencias) */}
      {tab === 0 && (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {solicitudes.filter(s => s.tecnico_id === user.id).map(s => (
            <Grid item xs={12} sm={6} key={s.id}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>{s.tipo === 'vacaciones' ? '🌴 Vacaciones' : '📅 Libre disposición'}</Typography>
                    <Chip label={s.estado} color={estadoColor[s.estado]} size="small" />
                  </Box>
                  <Typography variant="body2">{s.fecha_inicio} → {s.fecha_fin} ({s.dias_solicitados} días)</Typography>
                  {s.motivo && <Typography variant="caption" color="text.secondary" display="block">{s.motivo}</Typography>}
                </CardContent>
              </Card>
            </Grid>
          ))}
          {solicitudes.filter(s => s.tecnico_id === user.id).length === 0 && (
            <Grid item xs={12}><Alert severity="info">No tienes solicitudes registradas.</Alert></Grid>
          )}
        </Grid>
      )}

      {/* TAB JORNADA (Historial fichajes) */}
      {tab === 1 && (
        <Box>
          {/* Filtros */}
          <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField fullWidth size="small" label="Desde" type="date"
                InputLabelProps={{ shrink: true }}
                value={fichajeFiltros.desde}
                onChange={(e) => setFichajeFiltros({ ...fichajeFiltros, desde: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth size="small" label="Hasta" type="date"
                InputLabelProps={{ shrink: true }}
                value={fichajeFiltros.hasta}
                onChange={(e) => setFichajeFiltros({ ...fichajeFiltros, hasta: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button size="small" variant="text" onClick={() => setFichajeFiltros({ desde: '', hasta: '' })}>Limpiar</Button>
              <Button size="small" variant="contained" sx={{ bgcolor: '#4f46e5' }}
                onClick={exportarFichajesExcel} disabled={fichajesFiltrados.length === 0}>
                📊 Excel
              </Button>
            </Grid>
          </Grid>

          <Chip label={`${fichajesFiltrados.length} fichajes · Total: ${totalHorasPeriodo.toFixed(2)}h`}
            color="info" sx={{ mb: 2 }} />

          <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#1a1a2e' }}>
                  <TableCell sx={{ color: 'white' }}>Fecha</TableCell>
                  <TableCell sx={{ color: 'white' }}>Entrada</TableCell>
                  <TableCell sx={{ color: 'white' }}>Pausa</TableCell>
                  <TableCell sx={{ color: 'white' }}>Salida</TableCell>
                  <TableCell sx={{ color: 'white' }}>Horas</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fichajesFiltrados.map((f, i) => (
                  <TableRow key={i}>
                    <TableCell>{f.fecha}</TableCell>
                    <TableCell>{f.hora_entrada || '—'}</TableCell>
                    <TableCell>{f.hora_inicio_pausa ? `${f.hora_inicio_pausa} - ${f.hora_fin_pausa || '...'}` : '—'}</TableCell>
                    <TableCell>{f.hora_salida || '—'}</TableCell>
                    <TableCell>{f.horas_totales ? `${f.horas_totales}h` : '—'}</TableCell>
                  </TableRow>
                ))}
                {fichajesFiltrados.length === 0 && (
                  <TableRow><TableCell colSpan={5} align="center">No hay fichajes en este periodo</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* TAB SEGURIDAD — estado del 2FA (informativo) */}
      {tab === 3 && (
        <Alert severity="success" sx={{ mb: 3, maxWidth: 560 }}>
          🔐 <b>Verificación en dos pasos activada.</b> Tu cuenta está protegida con
          el código de tu móvil. Si cambias de teléfono, pide a un administrador que
          resetee tu verificación para configurarla de nuevo.
        </Alert>
      )}

      {/* TAB SEGURIDAD */}
      {tab === 3 && (
        <Card sx={{ borderRadius: 3, maxWidth: 500 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>🔒 Cambiar contraseña</Typography>
            {mensaje && <Alert severity={mensaje.tipo} sx={{ mb: 2 }}>{mensaje.texto}</Alert>}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField label="Contraseña actual" type="password" fullWidth value={passwordActual} onChange={(e) => setPasswordActual(e.target.value)} />
              <TextField label="Nueva contraseña" type="password" fullWidth value={passwordNueva} onChange={(e) => setPasswordNueva(e.target.value)} />
              <TextField label="Confirmar nueva contraseña" type="password" fullWidth value={passwordConfirmar} onChange={(e) => setPasswordConfirmar(e.target.value)} />
              <Button variant="contained" fullWidth onClick={handleCambiarPassword} sx={{ bgcolor: '#4f46e5', py: 1.2 }}>
                Cambiar contraseña
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* TAB EMPRESA (Organigrama) */}
      {tab === 2 && (
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>🏢 Organigrama de la empresa</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Estructura jerárquica de Grupo HEI. El director general está en la cima.
            </Typography>
            <Box sx={{ overflowX: 'auto', pb: 2 }}>
              <ArbolOrganigrama usuarios={organigrama} />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* MI DOCUMENTACIÓN (dentro de Empresa) */}
      {tab === 2 && (
        <Card sx={{ borderRadius: 3, mt: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>🔐 Mi documentación confidencial</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Aquí puedes consultar y descargar tus documentos personales (nóminas, contratos, etc.).
            </Typography>
            {miDocumentacion.length === 0 ? (
              <Alert severity="info">No tienes documentos disponibles todavía.</Alert>
            ) : (
              <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#1a1a2e' }}>
                      <TableCell sx={{ color: 'white' }}>Tipo</TableCell>
                      <TableCell sx={{ color: 'white' }}>Título</TableCell>
                      <TableCell sx={{ color: 'white' }}>Fecha</TableCell>
                      <TableCell sx={{ color: 'white' }}>Descargar</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {miDocumentacion.map((d) => (
                      <TableRow key={d.id}>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{d.tipo}</TableCell>
                        <TableCell>{d.titulo}</TableCell>
                        <TableCell>{d.fecha?.slice(0, 10)}</TableCell>
                        <TableCell>
                          <Button size="small" variant="outlined"
                            onClick={() => descargarDocumentacion(d.id, d.titulo)}>
                            ⬇ Descargar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* TAB APROBAR SOLICITUDES (responsables con equipo) */}
      {tab === 4 && puedeAprobar && (
        <Grid container spacing={2}>
          {solicitudes.filter(s => s.estado === 'pendiente').map(s => (
            <Grid item xs={12} sm={6} key={s.id}>
              <Card sx={{ borderRadius: 3, borderLeft: '4px solid #f59e0b' }}>
                <CardContent>
                  <Typography sx={{ fontWeight: 600 }}>{s.nombre} {s.apellidos}</Typography>
                  <Typography variant="body2">{s.tipo === 'vacaciones' ? '🌴 Vacaciones' : '📅 Libre disposición'}</Typography>
                  <Typography variant="body2">{s.fecha_inicio} → {s.fecha_fin} ({s.dias_solicitados} días)</Typography>
                  {s.motivo && <Typography variant="caption" color="text.secondary" display="block">{s.motivo}</Typography>}
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button size="small" variant="contained" sx={{ bgcolor: '#22c55e' }} onClick={() => handleResolver(s.id, 'aprobada')}>
                      ✅ Aprobar
                    </Button>
                    <Button size="small" variant="outlined" color="error" onClick={() => handleResolver(s.id, 'rechazada')}>
                      ❌ Rechazar
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {solicitudes.filter(s => s.estado === 'pendiente').length === 0 && (
            <Grid item xs={12}><Alert severity="info">No hay solicitudes pendientes de aprobar.</Alert></Grid>
          )}
        </Grid>
      )}
    </Box>
  );
}

// ── COMPONENTE ÁRBOL ORGANIGRAMA ──────────────────────────
function ArbolOrganigrama({ usuarios }: { usuarios: any[] }) {
  // Encontrar la raíz (sin responsable) y construir el árbol recursivo
  const raices = usuarios.filter(u => !u.responsable_id);

  const NodoUsuario = ({ usuario }: { usuario: any }) => {
    const hijos = usuarios.filter(u => u.responsable_id === usuario.id);
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Caja del usuario */}
        <Box sx={{
          border: '2px solid #F5C200',
          borderRadius: 2,
          px: 2, py: 1,
          bgcolor: 'background.paper',
          minWidth: 150,
          textAlign: 'center',
          boxShadow: 1,
          mb: hijos.length > 0 ? 2 : 0,
        }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
            {usuario.nombre} {usuario.apellidos}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {usuario.puesto || 'Sin puesto asignado'}
          </Typography>
        </Box>

        {/* Línea vertical hacia los hijos */}
        {hijos.length > 0 && (
          <>
            <Box sx={{ width: 2, height: 16, bgcolor: '#F5C200' }} />
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
              {hijos.map(hijo => (
                <Box key={hijo.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Box sx={{ width: 2, height: 16, bgcolor: '#F5C200' }} />
                  <NodoUsuario usuario={hijo} />
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>
    );
  };

  if (raices.length === 0) {
    return <Alert severity="info">No hay estructura jerárquica definida todavía.</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center', minWidth: 'fit-content' }}>
      {raices.map(raiz => <NodoUsuario key={raiz.id} usuario={raiz} />)}
    </Box>
  );
}

// ── PANEL DE VERIFICACIÓN EN DOS PASOS ────────────────────
function Panel2FA() {
  const [activo, setActivo] = useState(false);
  const [qr, setQr] = useState('');
  const [secreto, setSecreto] = useState('');
  const [codigo, setCodigo] = useState('');
  const [passwordDesactivar, setPasswordDesactivar] = useState('');
  const [msg, setMsg] = useState<{ tipo: 'success' | 'error' | 'info', texto: string } | null>(null);
  const [cargando, setCargando] = useState(false);

  const cargarEstado = () => {
    estado2FA().then((d: any) => setActivo(d.activo)).catch(() => {});
  };
  useEffect(() => { cargarEstado(); }, []);

  const generarQR = async () => {
    setCargando(true);
    setMsg(null);
    try {
      const d = await iniciar2FA();
      setQr(d.qr);
      setSecreto(d.secreto);
    } catch {
      setMsg({ tipo: 'error', texto: 'Error al generar el código QR' });
    } finally {
      setCargando(false);
    }
  };

  const confirmar = async () => {
    setCargando(true);
    setMsg(null);
    try {
      const d = await confirmar2FA(codigo.trim());
      setMsg({ tipo: 'success', texto: d.mensaje });
      setQr(''); setSecreto(''); setCodigo('');
      cargarEstado();
    } catch (e: any) {
      setMsg({ tipo: 'error', texto: e.response?.data?.detail || 'Código incorrecto' });
    } finally {
      setCargando(false);
    }
  };

  const desactivar = async () => {
    setCargando(true);
    setMsg(null);
    try {
      const d = await desactivar2FA(passwordDesactivar);
      setMsg({ tipo: 'info', texto: d.mensaje });
      setPasswordDesactivar('');
      cargarEstado();
    } catch (e: any) {
      setMsg({ tipo: 'error', texto: e.response?.data?.detail || 'Error al desactivar' });
    } finally {
      setCargando(false);
    }
  };

  return (
    <Card sx={{ borderRadius: 3, maxWidth: 560, mb: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="h6">🔐 Verificación en dos pasos</Typography>
          <Chip label={activo ? 'Activada' : 'Desactivada'}
            color={activo ? 'success' : 'default'} size="small" />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Añade una segunda comprobación al iniciar sesión: además de tu contraseña,
          un código de 6 dígitos que genera tu móvil con Microsoft Authenticator
          (o cualquier app de códigos).
        </Typography>

        {msg && <Alert severity={msg.tipo} sx={{ mb: 2 }}>{msg.texto}</Alert>}

        {!activo && !qr && (
          <Button variant="contained" onClick={generarQR} disabled={cargando}
            sx={{ bgcolor: '#4f46e5' }}>
            Activar verificación en dos pasos
          </Button>
        )}

        {!activo && qr && (
          <Box>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              1. Abre <b>Microsoft Authenticator</b> en tu móvil → Agregar cuenta →
              Otra cuenta → Escanear código QR.
            </Typography>
            <Box sx={{ textAlign: 'center', mb: 1.5 }}>
              <img src={qr} alt="Código QR" style={{ width: 200, borderRadius: 8 }} />
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Si no puedes escanear, introduce esta clave a mano: <b>{secreto}</b>
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              2. Escribe el código de 6 dígitos que muestra la app:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField size="small" value={codigo}
                onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputProps={{ inputMode: 'numeric', style: { letterSpacing: 6, textAlign: 'center', fontWeight: 700 } }}
                sx={{ width: 160 }} />
              <Button variant="contained" onClick={confirmar}
                disabled={cargando || codigo.length !== 6} sx={{ bgcolor: '#4f46e5' }}>
                Confirmar
              </Button>
            </Box>
          </Box>
        )}

        {activo && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Tu cuenta está protegida. Al iniciar sesión se te pedirá el código del móvil.
            </Alert>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Para desactivarla, confirma tu contraseña:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField size="small" type="password" label="Contraseña"
                value={passwordDesactivar}
                onChange={(e) => setPasswordDesactivar(e.target.value)} />
              <Button variant="outlined" color="error" onClick={desactivar}
                disabled={cargando || !passwordDesactivar}>
                Desactivar
              </Button>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
